import { Link } from "react-router-dom";
import { GitCompareArrows, MapPin, ImageOff } from "lucide-react";
import { fileUrl, formatPrice, formatDate } from "@/lib/api";
import { ConditionBadge, StateBadge, VerifiedTick, Stars } from "./Badges";
import { useCompare } from "@/context/CompareContext";

export function CompareToggle({ type, id, title, category }) {
  const { has, toggle } = useCompare();
  const on = has(type, id);
  return (
    <button type="button" data-testid={`compare-toggle-${type}-${id}`}
      onClick={(e) => { e.preventDefault(); toggle({ type, id, title, category }); }}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs transition-colors ${on ? "border-[#0071e3] bg-[#0071e3]/20 text-sky-700" : "border-black/[0.08] text-zinc-700 hover:border-black/20"}`}>
      <GitCompareArrows className="h-3.5 w-3.5" /> {on ? "Karşılaştırmada" : "Karşılaştır"}
    </button>
  );
}

const Thumb = ({ src, alt }) =>
  src ? (
    <img
      src={fileUrl(src)}
      alt={alt || "Ürün görseli"}
      className="h-full w-full object-cover"
      loading="lazy"
      decoding="async"
    />
  ) : (
    <div className="grid-bg flex h-full w-full flex-col items-center justify-center gap-1 text-zinc-400">
      <ImageOff className="h-7 w-7" />
      <span className="text-[11px]">Görsel yok</span>
    </div>
  );

export function ListingCard({ item }) {
  return (
    <Link to={`/ilan/${item.id}`} data-testid={`listing-card-${item.id}`} className="panel lift group flex flex-col overflow-hidden">
      <div className="aspect-[4/3] bg-[#f5f5f7] overflow-hidden"><Thumb src={item.photo} alt={item.title} /></div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex flex-wrap items-center gap-1.5"><StateBadge state={item.item_state} /><ConditionBadge code={item.condition_class} /></div>
        <h3 className="line-clamp-2 font-display text-base font-semibold leading-snug">{item.title}</h3>
        <p className="text-xs text-zinc-500">{item.product_title}</p>
        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          <span className="font-display text-xl font-semibold text-zinc-900" data-testid="listing-card-price">{formatPrice(item.price, item.currency)}</span>
          <CompareToggle type="listing" id={item.id} title={item.title} category={item.category} />
        </div>
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{item.district}, {item.province}</span>
          <span>{formatDate(item.created_at)}</span>
        </div>
        <span className="text-xs text-zinc-500">{item.seller?.full_name} <VerifiedTick show={item.seller?.is_verified_seller} /></span>
      </div>
    </Link>
  );
}

export function ProductCard({ p }) {
  return (
    <Link to={`/katalog/${p.id}`} data-testid={`product-card-${p.id}`} className="panel lift flex flex-col overflow-hidden">
      <div className="aspect-[16/10] bg-[#f5f5f7] overflow-hidden"><Thumb src={p.images?.[0]} alt={p.title} /></div>
      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <span className="eyebrow">{p.brand}</span>
        <h3 className="font-display font-semibold leading-snug">{p.model}{p.revision && <span className="ml-1.5 rounded bg-black/[0.05] px-1.5 text-xs font-mono">{p.revision}</span>}</h3>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <Stars value={p.rating?.average || 0} size={3} />
          <span>{p.rating?.count ? `${p.rating.average} (${p.rating.count})` : "Henüz yorum yok"}</span>
        </div>
        <div className="mt-auto flex items-center justify-between pt-3 text-xs">
          <span className="text-zinc-500">{p.active_listings ? `${p.active_listings} aktif ilan` : "Aktif ilan yok"}</span>
          <CompareToggle type="product" id={p.id} title={p.title} category={p.category} />
        </div>
      </div>
    </Link>
  );
}
